import { useState } from 'react';
import { useCaseDetail } from '@/hooks/useCaseDetail';
import { usePacketCompleteness } from '@/hooks/usePacketCompleteness';
import { useAuth } from '@/hooks/useAuth';
import { useDemoMode } from '@/contexts/DemoModeContext';
import { DemoPhase2Phase3Panels } from '@/components/demo/DemoPhase2Phase3Panels';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Separator } from '@/components/ui/separator';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { PacketCompletenessCard } from './PacketCompletenessCard';
import { PayerStatusTimeline } from './PayerStatusTimeline';
import { DenialCategoryBadge } from './DenialCategoryBadge';
import { AppealBuilder } from './AppealBuilder';
import { AddPayerStatus } from './AddPayerStatus';
import { DenialReasonEntry } from './DenialReasonEntry';
import { EvidenceBundleExportButton } from './EvidenceBundleExportButton';
import { SubmissionLanePanel } from './SubmissionLanePanel';
import { OpNoteInputPanel } from './OpNoteInputPanel';
import { OpNoteIngestionPanel } from '@/components/reconciliation/OpNoteIngestionPanel';
import { AuthorizationIntentPanel } from '@/components/compliance/AuthorizationIntentPanel';
import { EncryptionStatusBadge } from '@/components/security/EncryptionStatusBadge';
import { 
  User, 
  Building2, 
  FileText, 
  Upload, 
  Clock, 
  Calendar,
  Send,
  Phone,
  Globe,
  Printer,
  CheckCircle2,
  AlertCircle,
  Plus,
  History,
  Stethoscope,
  Shield,
  Loader2,
  X,
  Lock,
  AlertTriangle,
  TrendingUp,
  Scale
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { format, parseISO, formatDistanceToNow } from 'date-fns';
import { 
  STATUS_LABELS, 
  STATUS_COLORS, 
  URGENCY_COLORS,
  SUBMISSION_LANE_LABELS,
  FOLLOW_UP_TYPE_LABELS,
  AuthorizationStatus,
  SubmissionLane,
  FollowUpType
} from '@/lib/authorization/types';
import { NORMALIZED_STATUS_LABELS, NORMALIZED_STATUS_COLORS } from '@/lib/normalization/statusNormalization';
import type { DenialCategory } from '@/lib/normalization/denialNormalization';

interface CaseDetailDrawerProps {
  caseId: string | null;
  onClose: () => void;
}

const WORKFLOW_ACTIONS: Record<string, { label: string; nextStatus: AuthorizationStatus; color: string }[]> = {
  surgeon_approved: [
    { label: 'Generate Packet', nextStatus: 'packet_generated', color: 'bg-cyan-500' },
  ],
  packet_generated: [
    { label: 'Submit to Payer', nextStatus: 'submitted_to_payer', color: 'bg-indigo-500' },
  ],
  submitted_to_payer: [
    { label: 'Mark In Review', nextStatus: 'payer_pending', color: 'bg-purple-500' },
  ],
  payer_pending: [
    { label: 'Approved', nextStatus: 'payer_approved', color: 'bg-primary' },
    { label: 'Denied', nextStatus: 'payer_denied', color: 'bg-red-500' },
    { label: 'More Info Requested', nextStatus: 'more_info_requested', color: 'bg-orange-500' },
  ],
  more_info_requested: [
    { label: 'Info Submitted', nextStatus: 'payer_pending', color: 'bg-purple-500' },
  ],
  payer_denied: [
    { label: 'File Appeal', nextStatus: 'appeal_in_progress', color: 'bg-orange-500' },
    { label: 'Close Case', nextStatus: 'closed', color: 'bg-slate-500' },
  ],
  appeal_in_progress: [
    { label: 'Appeal Approved', nextStatus: 'payer_approved', color: 'bg-primary' },
    { label: 'Appeal Denied', nextStatus: 'payer_denied', color: 'bg-red-500' },
  ],
  payer_approved: [
    { label: 'Close Case', nextStatus: 'closed', color: 'bg-slate-500' },
  ],
};

export function CaseDetailDrawer({ caseId, onClose }: CaseDetailDrawerProps) {
  const { isLinkOps } = useAuth();
  const { isDemoMode } = useDemoMode();
  
  const {
    caseData,
    documents,
    followUps,
    activityLog,
    loading,
    updateCase,
    updateDocument,
    addFollowUp,
    completeFollowUp,
    logActivity,
    refetch,
  } = useCaseDetail({ caseId });

  // Packet completeness scoring
  const completeness = usePacketCompleteness(caseData, documents);

  const [activeTab, setActiveTab] = useState('overview');
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [showAuthFields, setShowAuthFields] = useState(false);
  
  // Auth result fields
  const [authNumber, setAuthNumber] = useState('');
  const [authEffectiveStart, setAuthEffectiveStart] = useState('');
  const [authEffectiveEnd, setAuthEffectiveEnd] = useState('');
  
  // Follow-up fields
  const [newFollowUpDate, setNewFollowUpDate] = useState('');
  const [newFollowUpType, setNewFollowUpType] = useState<FollowUpType>('call');
  const [newFollowUpNotes, setNewFollowUpNotes] = useState('');

  const handleStatusChange = async (nextStatus: AuthorizationStatus, actionLabel: string) => {
    if (!caseData) return;

    // SUBMISSION GATING: Check if trying to submit to payer
    if (nextStatus === 'submitted_to_payer' && !completeness.canSubmit) {
      toast.error('Cannot submit: Packet is incomplete. Please complete all required items or apply an override.');
      return;
    }
    
    setUpdatingStatus(true);
    try {
      const updates: Record<string, unknown> = { status: nextStatus };
      
      // Add timestamp for specific status transitions
      if (nextStatus === 'packet_generated') {
        updates.packet_generated_at = new Date().toISOString();
      } else if (nextStatus === 'submitted_to_payer') {
        updates.submitted_to_payer_at = new Date().toISOString();
        // Also update completeness score snapshot
        updates.completeness_score = completeness.score;
        updates.missing_items = completeness.missingItems;
      } else if (nextStatus === 'more_info_requested') {
        updates.more_info_requested_at = new Date().toISOString();
      } else if (nextStatus === 'appeal_in_progress') {
        updates.appeal_submitted_at = new Date().toISOString();
      } else if (nextStatus === 'closed') {
        updates.closed_at = new Date().toISOString();
      } else if (nextStatus === 'payer_approved') {
        updates.payer_response = 'approved';
        updates.payer_response_date = new Date().toISOString();
        setShowAuthFields(true);
      } else if (nextStatus === 'payer_denied') {
        updates.payer_response = 'denied';
        updates.payer_response_date = new Date().toISOString();
      }

      await updateCase(updates);
      await logActivity(actionLabel, { from_status: caseData.status, to_status: nextStatus });
      toast.success(`Status updated to ${STATUS_LABELS[nextStatus]}`);
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleSaveAuthDetails = async () => {
    if (!authNumber) {
      toast.error('Auth number is required');
      return;
    }

    try {
      await updateCase({
        auth_number: authNumber,
        auth_effective_start: authEffectiveStart || null,
        auth_effective_end: authEffectiveEnd || null,
      });
      await logActivity('Added authorization details', { auth_number: authNumber });
      toast.success('Authorization details saved');
      setShowAuthFields(false);
    } catch (error) {
      console.error('Error saving auth details:', error);
      toast.error('Failed to save authorization details');
    }
  };

  const handleAddFollowUp = async () => {
    if (!newFollowUpDate || !caseId) return;

    try {
      await addFollowUp({
        authorization_id: caseId,
        scheduled_date: new Date(newFollowUpDate).toISOString(),
        follow_up_type: newFollowUpType,
        notes: newFollowUpNotes || undefined,
        completed: false,
      });
      await logActivity(`Scheduled ${FOLLOW_UP_TYPE_LABELS[newFollowUpType]} follow-up`, { date: newFollowUpDate });
      toast.success('Follow-up scheduled');
      setNewFollowUpDate('');
      setNewFollowUpNotes('');
    } catch (error) {
      console.error('Error adding follow-up:', error);
      toast.error('Failed to schedule follow-up');
    }
  };

  const handleCompleteFollowUp = async (followUpId: string) => {
    try {
      await completeFollowUp(followUpId);
      await logActivity('Completed follow-up');
      toast.success('Follow-up marked complete');
    } catch (error) {
      console.error('Error completing follow-up:', error);
      toast.error('Failed to complete follow-up');
    }
  };

  const handleToggleDocument = async (docId: string, isUploaded: boolean) => {
    try {
      await updateDocument(docId, { is_uploaded: isUploaded, uploaded_at: isUploaded ? new Date().toISOString() : undefined });
      await logActivity(isUploaded ? 'Marked document as uploaded' : 'Unmarked document');
    } catch (error) {
      console.error('Error updating document:', error);
      toast.error('Failed to update document');
    }
  };

  if (!caseId) return null;

  return (
    <Sheet open={!!caseId} onOpenChange={(open) => !open && onClose()}>
      <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : !caseData ? (
          <div className="flex items-center justify-center h-full">
            <p className="text-muted-foreground">Case not found</p>
          </div>
        ) : (
          <>
            <SheetHeader className="space-y-4">
                <div className="flex items-start justify-between">
                <div>
                  <SheetTitle className="font-mono text-xl">{caseData.case_number}</SheetTitle>
                  <div className="flex items-center gap-2 mt-2">
                    <Badge className={cn(STATUS_COLORS[caseData.status as AuthorizationStatus])}>
                      {STATUS_LABELS[caseData.status as AuthorizationStatus]}
                    </Badge>
                    <Badge className={cn(URGENCY_COLORS[caseData.urgency])}>
                      {caseData.urgency}
                    </Badge>
                    <EncryptionStatusBadge />
                  </div>
                </div>
                
                {/* Evidence Bundle Export - LINK Ops only */}
                {isLinkOps && (
                  <EvidenceBundleExportButton
                    caseData={caseData}
                    documents={documents}
                    followUps={followUps}
                    activityLog={activityLog}
                    overrideInfo={completeness.isOverridden ? {
                      reason: completeness.overrideReason || 'Reason not specified',
                      overriddenBy: completeness.overrideBy || 'Unknown',
                      overriddenAt: completeness.overrideAt || new Date().toISOString(),
                    } : undefined}
                  />
                )}
              </div>

              {/* Workflow Actions */}
              {WORKFLOW_ACTIONS[caseData.status] && (
                <div className="flex flex-wrap gap-2">
                  {WORKFLOW_ACTIONS[caseData.status].map((action) => {
                    // Check if this is a submit action that should be gated
                    const isSubmitAction = action.nextStatus === 'submitted_to_payer';
                    const isBlocked = isSubmitAction && !completeness.canSubmit;
                    
                    return (
                      <Button
                        key={action.nextStatus}
                        size="sm"
                        disabled={updatingStatus || isBlocked}
                        className={cn(
                          action.color, 
                          'text-white hover:opacity-90',
                          isBlocked && 'opacity-50 cursor-not-allowed'
                        )}
                        onClick={() => handleStatusChange(action.nextStatus, action.label)}
                        title={isBlocked ? 'Packet incomplete - cannot submit' : undefined}
                      >
                        {updatingStatus ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : isBlocked ? (
                          <>
                            <Lock className="w-4 h-4 mr-1" />
                            {action.label}
                          </>
                        ) : (
                          action.label
                        )}
                      </Button>
                    );
                  })}
                </div>
              )}

              {/* Auth Details Entry (when approved) */}
              {showAuthFields && (
                <Card className="border-primary/30 bg-primary/5">
                  <CardContent className="p-4 space-y-3">
                    <p className="text-sm font-medium text-primary">Enter Authorization Details</p>
                    <div className="grid grid-cols-3 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs">Auth Number *</Label>
                        <Input
                          value={authNumber}
                          onChange={(e) => setAuthNumber(e.target.value)}
                          placeholder="Auth #"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Effective Start</Label>
                        <Input
                          type="date"
                          value={authEffectiveStart}
                          onChange={(e) => setAuthEffectiveStart(e.target.value)}
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Effective End</Label>
                        <Input
                          type="date"
                          value={authEffectiveEnd}
                          onChange={(e) => setAuthEffectiveEnd(e.target.value)}
                        />
                      </div>
                    </div>
                    <Button size="sm" onClick={handleSaveAuthDetails}>
                      Save Authorization Details
                    </Button>
                  </CardContent>
                </Card>
              )}

              {/* Display saved auth info */}
              {caseData.auth_number && (
                <Card className="border-primary/30 bg-primary/5">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2">
                      <Shield className="w-5 h-5 text-primary" />
                      <div>
                        <p className="text-sm font-medium">Auth #{caseData.auth_number}</p>
                        {caseData.auth_effective_start && (
                          <p className="text-xs text-muted-foreground">
                            Valid: {format(parseISO(caseData.auth_effective_start), 'MMM d, yyyy')}
                            {caseData.auth_effective_end && ` - ${format(parseISO(caseData.auth_effective_end), 'MMM d, yyyy')}`}
                          </p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </SheetHeader>

            <Separator className="my-4" />

            {/* Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid grid-cols-6 w-full">
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="compliance" className="flex items-center gap-1">
                  <Scale className="w-3 h-3" />
                  Compliance
                </TabsTrigger>
                <TabsTrigger value="documents">Docs</TabsTrigger>
                <TabsTrigger value="status">Status</TabsTrigger>
                <TabsTrigger value="followups">Follow-ups</TabsTrigger>
                <TabsTrigger value="activity">Activity</TabsTrigger>
              </TabsList>

              {/* Overview Tab */}
              <TabsContent value="overview" className="space-y-4 mt-4">
                {/* Packet Completeness - shown for cases awaiting submission */}
                {['surgeon_approved', 'packet_generated'].includes(caseData.status) && (
                  <PacketCompletenessCard
                    caseId={caseId!}
                    completeness={completeness}
                    logActivity={logActivity}
                    onRefetch={refetch}
                  />
                )}

                {/* Patient & Coverage */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <User className="w-4 h-4" />
                      Patient & Coverage
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Patient</span>
                      <span className="font-medium">{caseData.patient?.last_name}, {caseData.patient?.first_name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">MRN</span>
                      <span className="font-mono">{caseData.patient?.mrn}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">DOB</span>
                      <span>{caseData.patient?.dob}</span>
                    </div>
                    <Separator />
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Payer</span>
                      <span className="font-medium">{caseData.payer?.name}</span>
                    </div>
                    {caseData.facility && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Facility</span>
                        <span>{caseData.facility.name}</span>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Procedures */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Stethoscope className="w-4 h-4" />
                      Procedures
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">CPT Codes</p>
                      <div className="flex flex-wrap gap-1">
                        {caseData.cpt_codes.map((cpt) => (
                          <Badge key={cpt} variant="outline" className="font-mono">
                            {cpt}
                          </Badge>
                        ))}
                        {caseData.modifiers?.map((mod) => (
                          <Badge key={mod} variant="secondary" className="font-mono text-xs">
                            -{mod}
                          </Badge>
                        ))}
                      </div>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Diagnoses</p>
                      <div className="flex flex-wrap gap-1">
                        {caseData.icd10_codes.map((icd) => (
                          <Badge key={icd} variant="outline" className="font-mono text-xs">
                            {icd}
                          </Badge>
                        ))}
                      </div>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Description</p>
                      <p className="text-sm">{caseData.procedure_description}</p>
                    </div>
                  </CardContent>
                </Card>

                {/* Submission Lane - New Unified Panel */}
                <SubmissionLanePanel caseData={caseData} isLinkOps={isLinkOps} />

                {/* Notes */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Notes</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Textarea
                      value={caseData.notes || ''}
                      onChange={async (e) => {
                        await updateCase({ notes: e.target.value });
                      }}
                      placeholder="Add notes about this case..."
                      rows={3}
                    />
                  </CardContent>
                </Card>

                {/* Denial Info (if denied) */}
                {(caseData.status === 'payer_denied' || caseData.status === 'appeal_in_progress') && (
                  <Card className="border-red-500/30 bg-red-500/5">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm flex items-center gap-2 text-red-500">
                        <AlertTriangle className="w-4 h-4" />
                        Denial Information
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {caseData.denial_category && (
                        <div>
                          <p className="text-xs text-muted-foreground mb-1">Denial Category</p>
                          <DenialCategoryBadge
                            category={caseData.denial_category as DenialCategory}
                            confidence={caseData.denial_category_confidence || 'TEMPLATE'}
                            rawText={caseData.denial_raw_text}
                          />
                        </div>
                      )}
                      {caseData.denial_reason && (
                        <div>
                          <p className="text-xs text-muted-foreground mb-1">Denial Reason</p>
                          <p className="text-sm">{caseData.denial_reason}</p>
                        </div>
                      )}
                      {caseData.denial_raw_text && (
                        <div>
                          <p className="text-xs text-muted-foreground mb-1">Raw Payer Response</p>
                          <p className="text-sm italic text-muted-foreground">{caseData.denial_raw_text}</p>
                        </div>
                      )}
                      <Separator />
                      <AppealBuilder
                        authorization={caseData}
                        patient={caseData.patient}
                        payer={caseData.payer}
                        facility={caseData.facility}
                        onAppealCreated={() => {
                          toast.success('Appeal created successfully');
                          refetch();
                        }}
                      />
                    </CardContent>
                  </Card>
                )}
              </TabsContent>

              {/* Compliance Tab - Authorization Intent & Attestation */}
              <TabsContent value="compliance" className="space-y-4 mt-4">
                {/* Demo Mode: Force-show Phase 2 & 3 panels regardless of case status */}
                {isDemoMode && (
                  <DemoPhase2Phase3Panels 
                    authorizedCpts={caseData.authorized_cpts || caseData.cpt_codes}
                  />
                )}

                {/* Post-Op Documentation Input - Phase 3C+3D (production: only show for approved/reconciliation statuses) */}
                {!isDemoMode && ['payer_approved', 'awaiting_post_op_reconciliation', 'closed'].includes(caseData.status) && (
                  <OpNoteIngestionPanel
                    caseId={caseId}
                    authorizedCpts={caseData.authorized_cpts || caseData.cpt_codes}
                    onNoteUploaded={() => {
                      refetch();
                      toast.success('Operative note saved - ready for reconciliation');
                    }}
                    onRunReconciliation={() => {
                      refetch();
                    }}
                    onReconciliationComplete={() => {
                      refetch();
                      toast.success('Reconciliation complete');
                    }}
                  />
                )}

                <AuthorizationIntentPanel
                  primaryCpt={caseData.cpt_codes[0] || ''}
                  primaryCptDescription={caseData.procedure_description?.split(';')[0]?.split(' - ')[1] || undefined}
                  selectedSubsetCpts={caseData.selected_subset_cpts || []}
                  subsetTemplateId={caseData.subset_template_id}
                  subsetTemplateVersion={caseData.subset_template_version}
                  surgeonAttestation={caseData.surgeon_attestation || false}
                  attestationTimestamp={caseData.surgeon_attestation_timestamp}
                  surgeonId={caseData.surgeon_id}
                  authorizationScope={caseData.authorization_scope as 'SUPERSET' | 'STANDARD' | 'SINGLE' | null}
                  authorizedCpts={caseData.authorized_cpts || caseData.cpt_codes}
                  caseNumber={caseData.case_number}
                  createdAt={caseData.created_at}
                  onExportPdf={() => {
                    toast.info('PDF Export', {
                      description: 'Compliance PDF export will be available in a future update.',
                    });
                  }}
                />

                {/* Audit Trail Summary */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <History className="w-4 h-4" />
                      Recent Audit Events
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {activityLog.slice(0, 5).map((activity) => (
                        <div 
                          key={activity.id}
                          className="flex items-start gap-2 text-xs border-l-2 border-primary/30 pl-2"
                        >
                          <div className="flex-1">
                            <p className="font-medium">{activity.action}</p>
                            <p className="text-muted-foreground">
                              {format(parseISO(activity.created_at), 'MMM d, yyyy h:mm a')}
                            </p>
                          </div>
                        </div>
                      ))}
                      {activityLog.length === 0 && (
                        <p className="text-sm text-muted-foreground">No audit events recorded</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Status Timeline Tab */}
              <TabsContent value="status" className="space-y-4 mt-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center justify-between">
                      <span className="flex items-center gap-2">
                        <TrendingUp className="w-4 h-4" />
                        Payer Status Timeline
                      </span>
                      <AddPayerStatus authorizationId={caseId!} onStatusAdded={refetch} />
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {caseData.payer_normalized_status && (
                      <div className="mb-4 pb-4 border-b">
                        <p className="text-xs text-muted-foreground mb-2">Current Normalized Status</p>
                        <Badge className={cn(NORMALIZED_STATUS_COLORS[caseData.payer_normalized_status as keyof typeof NORMALIZED_STATUS_COLORS])}>
                          {NORMALIZED_STATUS_LABELS[caseData.payer_normalized_status as keyof typeof NORMALIZED_STATUS_LABELS]}
                        </Badge>
                        {caseData.payer_raw_status && (
                          <p className="text-xs text-muted-foreground mt-1">
                            Raw: {caseData.payer_raw_status}
                          </p>
                        )}
                      </div>
                    )}
                    <PayerStatusTimeline authorizationId={caseId!} />
                  </CardContent>
                </Card>

                {/* Denial Reason Entry - show when denied */}
                {(caseData.status === 'payer_denied' || caseData.status === 'appeal_in_progress') && (
                  <DenialReasonEntry
                    authorizationId={caseId!}
                    currentDenialReason={caseData.denial_reason}
                    currentDenialRawText={caseData.denial_raw_text}
                    onSaved={refetch}
                  />
                )}
              </TabsContent>

              {/* Documents Tab */}
              <TabsContent value="documents" className="space-y-4 mt-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center justify-between">
                      <span className="flex items-center gap-2">
                        <FileText className="w-4 h-4" />
                        Required Documents
                      </span>
                      <Badge variant="outline">
                        {documents.filter(d => d.is_uploaded).length}/{documents.length}
                      </Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {documents.length === 0 ? (
                      <p className="text-sm text-muted-foreground text-center py-4">
                        No documents required for this case
                      </p>
                    ) : (
                      <div className="space-y-2">
                        {documents.map((doc) => (
                          <div
                            key={doc.id}
                            className={cn(
                              "flex items-center justify-between p-3 rounded-lg",
                              doc.is_uploaded ? "bg-primary/10" : "bg-muted/50"
                            )}
                          >
                            <div className="flex items-center gap-3">
                              <Checkbox
                                checked={doc.is_uploaded}
                                onCheckedChange={(checked) => handleToggleDocument(doc.id, checked as boolean)}
                              />
                              <div>
                                <p className="text-sm font-medium">{doc.document_name}</p>
                                <p className="text-xs text-muted-foreground">{doc.document_type}</p>
                              </div>
                            </div>
                            {doc.is_required && (
                              <Badge variant="outline" className="text-xs">Required</Badge>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Follow-ups Tab */}
              <TabsContent value="followups" className="space-y-4 mt-4">
                {/* Add Follow-up */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Plus className="w-4 h-4" />
                      Schedule Follow-up
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs">Date & Time</Label>
                        <Input
                          type="datetime-local"
                          value={newFollowUpDate}
                          onChange={(e) => setNewFollowUpDate(e.target.value)}
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Type</Label>
                        <Select value={newFollowUpType} onValueChange={(v) => setNewFollowUpType(v as FollowUpType)}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {Object.entries(FOLLOW_UP_TYPE_LABELS).map(([value, label]) => (
                              <SelectItem key={value} value={value}>{label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <Input
                      placeholder="Notes (optional)"
                      value={newFollowUpNotes}
                      onChange={(e) => setNewFollowUpNotes(e.target.value)}
                    />
                    <Button size="sm" onClick={handleAddFollowUp} disabled={!newFollowUpDate}>
                      <Calendar className="w-4 h-4 mr-2" />
                      Schedule
                    </Button>
                  </CardContent>
                </Card>

                {/* Follow-up List */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      Scheduled Follow-ups
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {followUps.length === 0 ? (
                      <p className="text-sm text-muted-foreground text-center py-4">
                        No follow-ups scheduled
                      </p>
                    ) : (
                      <div className="space-y-2">
                        {followUps.map((fu) => (
                          <div
                            key={fu.id}
                            className={cn(
                              "flex items-center justify-between p-3 rounded-lg",
                              fu.completed ? "bg-muted/30 opacity-60" : "bg-muted/50"
                            )}
                          >
                            <div className="flex items-center gap-3">
                              {fu.completed ? (
                                <CheckCircle2 className="w-5 h-5 text-primary" />
                              ) : (
                                <Clock className="w-5 h-5 text-muted-foreground" />
                              )}
                              <div>
                                <p className="text-sm font-medium">{FOLLOW_UP_TYPE_LABELS[fu.follow_up_type as FollowUpType]}</p>
                                <p className="text-xs text-muted-foreground">
                                  {format(parseISO(fu.scheduled_date), 'MMM d, yyyy h:mm a')}
                                </p>
                                {fu.notes && <p className="text-xs mt-1">{fu.notes}</p>}
                              </div>
                            </div>
                            {!fu.completed && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleCompleteFollowUp(fu.id)}
                              >
                                Complete
                              </Button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Activity Tab */}
              <TabsContent value="activity" className="space-y-4 mt-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <History className="w-4 h-4" />
                      Audit Log
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {activityLog.length === 0 ? (
                      <p className="text-sm text-muted-foreground text-center py-4">
                        No activity recorded
                      </p>
                    ) : (
                      <div className="space-y-3">
                        {activityLog.map((entry) => (
                          <div key={entry.id} className="flex gap-3 text-sm">
                            <div className="w-2 h-2 rounded-full bg-primary mt-2 shrink-0" />
                            <div className="flex-1">
                              <p className="font-medium">{entry.action}</p>
                              <p className="text-xs text-muted-foreground">
                                {entry.actor_type} • {formatDistanceToNow(parseISO(entry.created_at), { addSuffix: true })}
                              </p>
                              {entry.details && (
                                <pre className="text-xs bg-muted/50 p-2 rounded mt-1 overflow-x-auto">
                                  {JSON.stringify(entry.details, null, 2)}
                                </pre>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
